import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import UploadFileIcon from '@mui/icons-material/UploadFile';
// import StarIcon from '@mui/icons-material/Star';
import {
  Alert,
  Box,
  Button,
  Grid,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  // List,
  // ListItem,
  // ListItemText,
  Typography,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import React, { useEffect, useRef, useState } from 'react';
import type { Resolver } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import * as Yup from 'yup';

import type { ContactUsType } from '@/types';
import { PartnerType } from '@/types/enumTypes';

import CustomTextMask from './CustomTextMask';
import Loader from './Loader';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(0),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));
interface ContactUsPayload {
  Name: string;
  Email: string;
  Phone: string;
  Message: string;
  Token: string;
  ExcelTemplate: string; // base64
}
export interface DialogTitleProps {
  id: string;
  children?: React.ReactNode;
  onClose: () => void;
  isOpenPopUp?: boolean;
  setIsOpenPopUp?: (value: boolean) => void;
}
export interface Form2290TemplateProps {
  isOpenPopUp: boolean;
  setIsOpenPopUp: React.Dispatch<React.SetStateAction<boolean>>;
}

function BootstrapDialogTitle(props: DialogTitleProps) {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, p: 0 }} {...other}>
      {children}
      {onClose ? (
        <Tooltip title="Close" arrow>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              p: 0,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      ) : null}
    </DialogTitle>
  );
}

const Form2290ExcelTemplateContactUs = (props: Form2290TemplateProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [openSnack, setSnackOpen] = useState(false);

  const handleSnackClose = () => {
    setSnackOpen(false);
  };

  const validationSchema = Yup.object().shape(
    {
      Name: Yup.string().required('Name is required'),
      Email: Yup.string()
        .required('Email Address is required')
        .email('Enter valid Email Address'),
      Phone: Yup.string()
        .required('Phone Number is required')
        .matches(
          /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}$/,
          'Phone Number must in the format (000) 000-0000'
        ),
      ExcelTemplate: Yup.mixed<File>()
        .required('Excel Template is required')
        .test(
          'filePresent',
          'Excel Template is required',
          (value) => value instanceof File
        )
        .test(
          'fileType',
          'Upload a valid Excel file (.xlsx or .xls)',
          (value) => {
            if (!(value instanceof File)) return true; // let "required" handle the empty case
            const name = value.name.toLowerCase();
            return name.endsWith('.xlsx') || name.endsWith('.xls');
          }
        ),
    },
    [
      // Add Cyclic deps here because when require itself
      ['Phone', 'Phone'],
    ]
  );

  const defaultValue: ContactUsType = {
    Email: '',
    Name: '',
    Phone: '',
    Message: '',
    Token: '',
    ExcelTemplate: null,
    DesignationOfContactPerson: '',
    NatureOfBusiness: '',
    PartnerName: '',
    PartnerType: PartnerType.None,
    Website: '',
  };

  const { handleSubmit, control, reset } = useForm<ContactUsType>({
    mode: 'onTouched',
    resolver: yupResolver(validationSchema) as unknown as Resolver<
      ContactUsType,
      any
    >,
    defaultValues: defaultValue,
  });

  React.useEffect(() => {
    const loadScriptByURL = (id: any, url: any, callback: any) => {
      const isScriptExist = document.getElementById(id);

      if (!isScriptExist) {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = url;
        script.id = id;
        // eslint-disable-next-line func-names
        script.onload = function () {
          if (callback) callback();
        };
        document.body.appendChild(script);
      }

      if (isScriptExist && callback) callback();
    };

    // load the script by passing the URL
    loadScriptByURL(
      'recaptcha-key',
      `https://www.google.com/recaptcha/api.js?render=${process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}&badge=bottomleft`,
      // eslint-disable-next-line func-names
      function () {
        // console.log('Script loaded!');
      }
    );
  }, []);

  function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(',')[1] ?? ''); // strip the "data:...;base64," prefix
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  const onSubmit = async (data: ContactUsType) => {
    setIsLoading(true);
    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY, {
          action: 'submit',
        })
        .then(async (token: any) => {
          const postData: ContactUsPayload = {
            Name: data.Name,
            Email: data.Email,
            Phone: data.Phone ?? '',
            Message: data.Message,
            Token: token,
            ExcelTemplate: data.ExcelTemplate
              ? await fileToBase64(data.ExcelTemplate)
              : '',
          };

          const address = `/api/contactus/`;
          const contactusResponse = await fetch(address, {
            method: 'POST',
            body: JSON.stringify(postData), // JSON stays — endpoint untouched
          });
          const contactusResponseData = await contactusResponse.json();
          if (contactusResponseData?.status === 'Success') {
            reset();
            setSnackOpen(true);
          }
          setIsLoading(false);
        })
        .catch(() => {});
    });
  };

  const handleClose = () => {
    props.setIsOpenPopUp(false);
  };

  useEffect(() => {
    reset(defaultValue);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [props.isOpenPopUp]);

  return (
    <BootstrapDialog
      onClose={handleClose}
      aria-labelledby="customized-dialog-title"
      open={props.isOpenPopUp}
      className="holiday-theme"
      fullWidth
      maxWidth="md"
    >
      <Loader isLoading={isLoading} />
      <BootstrapDialogTitle
        id="customized-dialog-title"
        onClose={handleClose}
      />
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={openSnack}
        autoHideDuration={6000}
        onClose={handleSnackClose}
      >
        <Alert
          onClose={handleSnackClose}
          severity="success"
          sx={{
            width: '100%',
            backgroundColor: '#198754',
            color: '#fff',
            fontWeight: 600,
          }}
        >
          Thanks for contacting us! We will be in touch with you shortly.
        </Alert>
      </Snackbar>
      <DialogContent>
        <Stack
          direction={{ xs: 'column-reverse', sm: 'row' }}
          justifyContent="space-between"
          alignItems="stretch"
          spacing={0}
        >
          <Box
            component="form"
            autoComplete="off"
            onSubmit={handleSubmit(onSubmit)}
            sx={{
              width: '100%',
              p: 3,
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontWeight: 600,
                color: '#000000',
                fontSize: { md: 28, sm: 20, xs: 18 },
                mb: 2,
              }}
            >
              Upload Your Completed <br />
              <span style={{ fontWeight: 700, color: '#1da94a' }}>
                2290 Template
              </span>{' '}
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontSize: 17, fontWeight: 500, mb: 2 }}
            >
              Upload your 2290 template now and receive your <br /> IRS-Stamped
              Schedule 1{' '}
            </Typography>
            <Box sx={{ textAlign: 'center' }}>
              <Grid size={12} sx={{}}>
                <Controller
                  name="Name"
                  control={control}
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <TextField
                      id="UserName"
                      name="UserName"
                      label="*Name"
                      color="secondary"
                      variant="outlined"
                      className={value ? 'prefilled' : 'mandatory'}
                      value={value}
                      onChange={onChange}
                      error={!!error}
                      helperText={error ? error.message : null}
                      inputProps={{
                        maxLength: 100,
                        maxHight: 100,
                      }}
                      fullWidth
                      sx={{ mb: 2 }}
                    />
                  )}
                />
              </Grid>
              <Grid size={12} sx={{}}>
                <Controller
                  name="Email"
                  control={control}
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <TextField
                      className={value ? 'prefilled' : 'mandatory'}
                      id="EmailAddress"
                      label="*Email Address"
                      variant="outlined"
                      autoComplete="nope"
                      color="secondary"
                      inputProps={{
                        maxLength: 100,
                      }}
                      value={value}
                      onChange={onChange}
                      error={!!error}
                      helperText={error ? error.message : null}
                      fullWidth
                      sx={{ mb: 2 }}
                    />
                  )}
                />
              </Grid>

              <Grid size={12} sx={{}}>
                <Controller
                  name="Phone"
                  control={control}
                  defaultValue=""
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <TextField
                      id="Phone"
                      className={value ? 'prefilled' : 'mandatory'}
                      label="*Phone Number"
                      variant="outlined"
                      color="secondary"
                      value={value}
                      onChange={onChange}
                      error={!!error}
                      helperText={error ? error.message : null}
                      InputProps={{
                        inputComponent: CustomTextMask as any,
                        inputProps: {
                          mask: '(#00) 000-0000',
                          maxLength: 14,
                        },
                      }}
                      sx={{ mb: 2 }}
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid size={12} sx={{}}>
                <Controller
                  name="Message"
                  control={control}
                  defaultValue=""
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <TextField
                      id="Message"
                      label="Message"
                      multiline
                      rows={4}
                      color="secondary"
                      value={value}
                      onChange={onChange}
                      error={!!error}
                      helperText={error ? error.message : null}
                      sx={{ mb: 2 }}
                      type="text"
                      fullWidth
                    />
                  )}
                />
              </Grid>

              {/* Email Excel Template — mandatory upload */}
              <Grid size={12} sx={{}}>
                <Controller
                  name="ExcelTemplate"
                  control={control}
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => {
                    const hasError = !!error;
                    return (
                      <Box sx={{ mb: 2, textAlign: 'left' }}>
                        <input
                          ref={fileInputRef}
                          id="ExcelTemplateInput"
                          type="file"
                          accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                          hidden
                          onChange={(e) =>
                            onChange(e.target.files?.[0] ?? null)
                          }
                        />

                        {value ? (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                              p: 1.5,
                              border: '1px solid',
                              borderColor: 'success.main',
                              borderRadius: 1,
                              backgroundColor: 'rgba(29, 169, 74, 0.06)',
                            }}
                          >
                            <DescriptionIcon sx={{ color: 'success.main' }} />
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography
                                noWrap
                                variant="body2"
                                sx={{ fontWeight: 600 }}
                              >
                                {value.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {(value.size / 1024).toFixed(1)} KB
                              </Typography>
                            </Box>
                            <Tooltip title="Remove" arrow>
                              <IconButton
                                size="small"
                                aria-label="Remove Email Excel Template"
                                onClick={() => {
                                  onChange(null);
                                  if (fileInputRef.current)
                                    fileInputRef.current.value = '';
                                }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Box
                            role="button"
                            tabIndex={0}
                            onClick={() => fileInputRef.current?.click()}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                fileInputRef.current?.click();
                              }
                            }}
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 0.5,
                              p: 2.5,
                              border: '1px dashed',
                              borderColor: hasError
                                ? 'error.main'
                                : 'rgba(0, 0, 0, 0.23)',
                              borderRadius: 1,
                              cursor: 'pointer',
                              transition: 'border-color 0.2s',
                              '&:hover': {
                                borderColor: hasError
                                  ? 'error.main'
                                  : 'secondary.main',
                              },
                            }}
                          >
                            <UploadFileIcon
                              sx={{
                                color: hasError
                                  ? 'error.main'
                                  : 'text.secondary',
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600 }}
                            >
                              Upload Form 2290 Template*
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Click to browse — .xlsx or .xls
                            </Typography>
                          </Box>
                        )}

                        {hasError ? (
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'error.main',
                              display: 'block',
                              mt: 0.5,
                              ml: 1.75,
                            }}
                          >
                            {error?.message}
                          </Typography>
                        ) : null}
                      </Box>
                    );
                  }}
                />
              </Grid>

              <Button
                variant="contained"
                size="large"
                type="submit"
                sx={{
                  textTransform: 'none',
                  boxShadow: 'none',
                  borderRadius: 4,
                  display: 'inline-block',
                  fontSize: { xs: 18, sm: 20, md: 22, lg: 24 },
                }}
              >
                Upload Now
              </Button>
              <Alert severity="info" sx={{ mt: 2 }} icon={false}>
                <Typography variant="body2" sx={{ lineHeight: 1.3 }}>
                  *Your data is safe with us. All information and documents are
                  securely protected and used only to complete your Form 2290
                  filing.{' '}
                </Typography>
              </Alert>
            </Box>
          </Box>
          <Box
            sx={{
              maxWidth: 300,
              width: '100%',
              minHeight: { xs: 339, sm: 380, md: 450 },
              minWidth: { xs: 200, sm: 339, md: 370 },
              display: { xs: 'none', sm: 'block' },
            }}
            className="template-upload"
          />
        </Stack>
      </DialogContent>
    </BootstrapDialog>
  );
};

export default Form2290ExcelTemplateContactUs;
